import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { MapPin, ArrowLeft, Loader2, AlertCircle, TrendingUp, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND_OPTIONS = [
  'JET','Shell','Aral','Esso','TotalEnergies','OMV','AVIA',
  'Agip / Eni','HEM','Star','Orlen','Raiffeisen',
  'RAN (Südramol)','Freie Tankstelle / Sonstige'
];

export const LocationNew = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [costPreview, setCostPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isTankstelle, setIsTankstelle] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    station_number: '',
    employee_count: 0,
    // employee_count wird nicht mehr im Formular angezeigt (firmenweit, nicht pro Standort)
    brand: ''
  });

  useEffect(() => {
    fetchCostPreview();
    // Check if customer is Tankstelle via user context or API
    checkCustomerIndustry();
  }, []);

  const checkCustomerIndustry = async () => {
    try {
      // First check from user context (already loaded)
      if (user?.industry === 'tankstelle') {
        setIsTankstelle(true);
        return;
      }
      // Fallback: fetch from API
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/admin/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.industry === 'tankstelle') setIsTankstelle(true);
    } catch {
      // silently ignore
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const fetchCostPreview = async () => {
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/admin/cost-preview/add-location`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCostPreview(response.data);
    } catch (error) {
      console.error('Error fetching cost preview:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Bitte geben Sie einen Standortnamen ein');
      return;
    }
    if (isTankstelle && !formData.brand) {
      toast.error('Bitte wählen Sie den Gesellschafter / die Marke');
      return;
    }

    // Preisvorschau laden und Dialog anzeigen
    await fetchCostPreview();
    setShowConfirmDialog(true);
  };

  const confirmCreate = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/admin/locations`,
        formData,
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success('Standort erfolgreich hinzugefügt');
      setShowConfirmDialog(false);
      navigate('/admin/locations');
    } catch (error) {
      console.error('Error creating location:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Standorts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/locations')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zu Standorte
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Neuer Standort</h1>
              <p className="text-sm text-muted-foreground">
                Fügen Sie einen neuen Standort zu Ihrem Unternehmen hinzu
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>Standort-Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">
                  Standortname <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="z.B. Tankstelle Hauptstraße"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </div>

              {/* Address with Autocomplete */}
              <div className="space-y-2">
                <Label htmlFor="address">
                  Adresse
                </Label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(value) => handleChange('address', value)}
                  placeholder="Straße, PLZ, Stadt..."
                />
                <p className="text-xs text-muted-foreground">
                  💡 Beginnen Sie mit der Eingabe für Adressvorschläge
                </p>
              </div>

              {/* Station Number */}
              <div className="space-y-2">
                <Label htmlFor="station_number">
                  Stationsnummer (optional)
                </Label>
                <Input
                  id="station_number"
                  placeholder="z.B. 12345"
                  value={formData.station_number}
                  onChange={(e) => handleChange('station_number', e.target.value)}
                />
              </div>

              {/* Gesellschafter / Marke – nur bei Tankstelle */}
              {isTankstelle && (
                <div className="space-y-2">
                  <Label htmlFor="brand">
                    Gesellschafter / Marke <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.brand}
                    onValueChange={(v) => handleChange('brand', v)}
                  >
                    <SelectTrigger id="brand">
                      <SelectValue placeholder="Marke auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {BRAND_OPTIONS.map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Bestimmt die Artikelnummern aus der Master-Datenbank für diesen Standort.
                  </p>
                </div>
              )}

              {/* Preisvorschau – direkt auf der Seite sichtbar, nicht erst im Dialog */}
              {loadingPreview ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Preisdetails werden geladen…
                </div>
              ) : costPreview && costPreview.billing_model === 'billing_v2' ? (
                costPreview.limit_reached ? (
                  /* Limit voll: Upgrade-Hinweis statt Preisinfo */
                  <div className="rounded-xl border-2 border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 p-4 space-y-3">
                    <p className="text-sm font-semibold text-orange-900 dark:text-orange-100 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      Standortlimit erreicht
                    </p>
                    <p className="text-sm text-orange-800 dark:text-orange-200">
                      Sie nutzen aktuell <strong>{costPreview.current_location_slots}/{costPreview.current_location_slots}</strong> gebuchte Standortplätze.
                      Für einen weiteren Standort benötigen Sie einen zusätzlichen Standortplatz.
                    </p>
                    <div className="text-xs text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30 rounded-lg p-2">
                      Zusatzstandort: <strong>+{costPreview.additional_location_unit_net?.toFixed(2)} €/Monat</strong>
                      {' '}→ Neuer Preis: <strong>{costPreview.new_total_net?.toFixed(2)} €/Monat</strong>
                      <span className="font-normal"> zzgl. 19% MwSt.</span>
                    </div>
                  </div>
                ) : (
                  /* Slots frei: Preisaufschlüsselung */
                  <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4 space-y-2">
                    <p className="text-sm font-semibold text-green-900 dark:text-green-100 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      Preisvorschau
                    </p>
                    <div className="text-sm text-green-800 dark:text-green-200 space-y-1">
                      <div className="flex justify-between">
                        <span>Paket ({costPreview.package_name}):</span>
                        <span className="font-semibold">{costPreview.base_package_net?.toFixed(2)} € Netto</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Zusatzstandort:</span>
                        <span className="font-semibold">+{costPreview.additional_location_unit_net?.toFixed(2)} € Netto</span>
                      </div>
                      <div className="flex justify-between border-t pt-1 mt-1 font-bold">
                        <span>Neuer Preis:</span>
                        <div className="text-right">
                          <span className="text-green-700 dark:text-green-300">
                            {costPreview.new_total_net?.toFixed(2)} € / {costPreview.interval_unit}
                          </span>
                          <p className="text-xs font-normal text-muted-foreground">zzgl. 19% MwSt.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : costPreview && costPreview.billing_model === 'legacy_v1' ? (
                /* Legacy V1 Preisinfo */
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    <strong>Kostenvorschau:</strong> Aktuell {costPreview.current?.total_cost?.toFixed(2)} €/Monat
                    → Nach Hinzufügen: <strong>{costPreview.new?.total_cost?.toFixed(2)} €/Monat</strong>
                    {' '}(+{costPreview.increase?.toFixed(2)} €)
                  </p>
                </div>
              ) : (
                /* Fallback vage Infobox */
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    <strong>💡 Hinweis:</strong> Mit jedem weiteren Standort passt sich Ihr Abonnementpreis automatisch an.
                    Prüfen Sie die Preisdetails unter <span className="font-semibold">Abonnement</span>.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                {costPreview?.limit_reached ? (
                  /* Limit voll: Upgrade-Button statt Erstellen */
                  <Button
                    type="button"
                    onClick={() => navigate('/admin/subscription')}
                    className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="upgrade-plan-btn"
                  >
                    <ArrowUpRight className="h-4 w-4 mr-2" />
                    Abo upgraden – weiteren Standortplatz buchen
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto"
                    data-testid="create-location-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Wird erstellt...
                      </>
                    ) : (
                      <>
                        <MapPin className="h-4 w-4 mr-2" />
                        Standort hinzufügen
                      </>
                    )}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/locations')}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* 🆕 CONFIRMATION DIALOG */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                Standort hinzufügen bestätigen
              </DialogTitle>
            </DialogHeader>
            
            {costPreview && (
              <div className="space-y-4">
                {/* V2: Neue strukturierte Anzeige */}
                {costPreview.billing_model === 'billing_v2' ? (
                  <>
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                        {costPreview.package_name} · {costPreview.interval_label}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        Aktuell: {costPreview.current_location_slots} Standortplatz · {costPreview.current_total_net.toFixed(2)} € Netto
                      </p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-2 text-sm">
                      <p className="font-semibold text-green-900 dark:text-green-100">Nach Lizenz-Erweiterung:</p>
                      <div className="flex justify-between">
                        <span>Paket ({costPreview.package_name}):</span>
                        <span className="font-semibold">{costPreview.base_package_net.toFixed(2)} € Netto</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Zusatzstandort-Preis:</span>
                        <span className="font-semibold">+{costPreview.additional_location_unit_net.toFixed(2)} € Netto</span>
                      </div>
                      <div className="border-t pt-2 mt-2 space-y-1">
                        <div className="flex justify-between font-bold">
                          <span>Gesamt Netto:</span>
                          <span className="text-green-700">{costPreview.new_total_net.toFixed(2)} € / {costPreview.interval_unit}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>zzgl. {costPreview.tax_rate}% MwSt.:</span>
                          <span>+{costPreview.tax_amount.toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold">
                          <span>Brutto gesamt:</span>
                          <span>{costPreview.total_gross.toFixed(2)} € / {costPreview.interval_unit}</span>
                        </div>
                      </div>
                    </div>
                    {/* Kein "sofort fällig" – Standort wird Draft */}
                    <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-700 rounded-lg p-3">
                      <p className="text-xs text-amber-900 dark:text-amber-100 font-semibold mb-1">Wichtig:</p>
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        Der Standort wird als <strong>Entwurf</strong> gespeichert. Keine Zahlung wird jetzt ausgeführt. Die Aktivierung erfolgt nach Buchung der Zusatzstandort-Lizenz.
                      </p>
                    </div>
                  </>
                ) : (
                  /* Legacy V1 – unverändert */
                  <>
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="font-semibold text-blue-900 dark:text-blue-100 mb-2">ℹ️ Kostenübersicht</p>
                      <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                        Der neue Standort <strong>"{formData.name || 'Unbenannt'}"</strong> wird zu Ihrem Abonnement hinzugefügt.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-3 sm:p-4">
                      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-sm sm:text-base">AKTUELL:</p>
                      <div className="space-y-1 text-xs sm:text-sm">
                        <div className="flex justify-between">
                          <span>Standorte:</span>
                          <span className="font-semibold">{costPreview.current.location_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Standortkosten:</span>
                          <span className="font-semibold">{costPreview.current.location_cost.toFixed(2)}€/Monat</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Mitarbeiter:</span>
                          <span className="font-semibold">{costPreview.current.employee_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Mitarbeiterkosten:</span>
                          <span className="font-semibold">{costPreview.current.employee_cost.toFixed(2)}€/Monat</span>
                        </div>
                        <div className="flex justify-between font-bold border-t pt-1 mt-1">
                          <span>Gesamt:</span>
                          <span>{costPreview.current.total_cost.toFixed(2)}€/Monat</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-3 sm:p-4">
                      <p className="font-semibold text-green-900 dark:text-green-100 mb-3 text-sm sm:text-base">NEU:</p>
                      <div className="space-y-1 text-xs sm:text-sm">
                        <div className="flex justify-between">
                          <span>Standorte:</span>
                          <span className="font-semibold">{costPreview.new.location_count} (+1)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Standortkosten:</span>
                          <span className="font-semibold">{costPreview.new.location_cost.toFixed(2)}€/Monat</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Mitarbeiter:</span>
                          <span className="font-semibold">{costPreview.new.employee_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Mitarbeiterkosten:</span>
                          <span className="font-semibold">{costPreview.new.employee_cost.toFixed(2)}€/Monat</span>
                        </div>
                        <div className="flex justify-between font-bold border-t pt-1 mt-1 text-base sm:text-lg">
                          <span>Gesamt:</span>
                          <span className="text-green-700">{costPreview.new.total_cost.toFixed(2)}€/Monat</span>
                        </div>
                      </div>
                    </div>

                    {costPreview.increase > 0 && (
                      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                        <p className="text-sm text-amber-900 dark:text-amber-100">
                          <strong><TrendingUp className="inline h-4 w-4 mr-1" />Erhöhung:</strong> +{costPreview.increase.toFixed(2)}€/Monat
                        </p>
                        <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                          Anteilige Abrechnung: ~{costPreview.prorated_charge.toFixed(2)}€ sofort fällig
                        </p>
                      </div>
                    )}

                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2 sm:p-3">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <strong>Tipp:</strong> Mit dem neuen Standort erhöhen Sie auch Ihr Mitarbeiter-Kontingent um 10 kostenlose Mitarbeiter!
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmDialog(false)}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                Abbrechen
              </Button>
              <Button 
                onClick={confirmCreate}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Wird erstellt...
                  </>
                ) : (
                  'Standort hinzufügen'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
